import difference from 'lodash/difference'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import { action, computed, makeObservable, observable } from 'mobx'
import { dependsOn, getValue, isPending, trackedAction } from 'mobx-async'
import { computedRouteParam } from '@stores/computedRouteParam'
import { computedFn } from 'mobx-utils'

import {
  Granularity,
  GuestReadinessStatus,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  Alert,
  formatDate,
  getDatesFromPeriod,
  getPeriodOption,
  PeriodPresets,
} from '@avantstay/backoffice-core'

import {
  CustomFieldBySectionId,
  CustomFieldProjectionProps,
} from '@new/__global/components/CustomField/_types'
import { IFieldsViewMode } from '@new/__global/components/CustomFieldSection/_types'
import { parseCustomFieldDefaultValue } from '@new/__global/components/CustomFieldSection/utils'
import { FieldVerificationProps } from '@new/__global/utils/getVerificationFieldsProgress'
import { handleStoreError } from '@new/__global/utils/handleStoreError'
import { hotelTapeChartPath } from '@new/domains/hotelTapeChart/config/contants'
import {
  CancellationPolicyCategoriesResults,
  ExtendedHomeViewLocation,
} from '@new/domains/properties/_types'
import { propertyInfoRoutes } from '@new/domains/properties/constants'
import { UpdateCancellationPoliciesProps } from '@new/domains/properties/screens/propertyDistributionChannels/CancellationPolicies/CancellationPolicies.types'
import {
  getValidPropertyBasicField,
  parseHomeFieldToBasicForm,
  propertyInfoCustomFieldsFilter,
} from '@new/domains/properties/screens/propertyInfo/_utils'
import {
  BasicFieldsView,
  PropertyInfoFields,
  SubmitBasicInfoProps,
} from '@new/domains/properties/screens/propertyInfo/PropertyInfo.types'
import { MapMarkerType } from '@new/domains/properties/screens/viewMode/components/Map/Map.types'
import { MapHome } from '@new/domains/properties/screens/viewMode/ViewMode.types'
import { PropertyCustomFieldsStore } from '@new/domains/properties/stores/PropertyCustomFieldsStore'

import { arriere } from '@arriere/index'
import {
  getPropertyById,
  getPropertyByIdHeaderInfo,
  getPropertyByIdLocationSummary,
  getPropertyFamilyStatus,
  getPropertyHealthTags,
  propertyInfoFieldsToVerify,
  updateHomeData,
  verifyPropertyInfo,
} from '@arriere/modules/properties'
import {
  HomeFieldsVerifyView,
  HomeViewCustomFields,
  HomeViewLocationSummaryProps,
  HomeViewProps,
  PropertyFamilyStatus,
} from '@arriere/types/Properties.types'
import { ROUTES } from '@routes'
import { trackError, trackEvent } from '@tracking/customEventReport'
import {
  filterCustomFieldsByIds,
  filterCustomFieldsBySearchTerm,
  filterCustomFieldsBySectionIds,
  getCustomFields,
  isFieldEmpty,
  jsonParseCustomFieldValue,
  newNormalizeCustomFields,
  newParseCustomFieldsToSave,
  ONBOARDING_SECTION_ID,
  parseSectionFields,
  PROPERTY_TYPE_CUSTOM_FIELD_ID,
  REVENUE_CONFIGURATION_SECTION_ID,
  SQUARE_FOOTAGE_CUSTOM_FIELD_ID,
} from '@utils/customFields'

type HomeCustomField = HomeViewCustomFields['fields'][number]
type HomeCustomFieldSection = HomeViewCustomFields['section']

class _PropertyInfoStore {
  filterTerm = ''

  unfilteredCustomFields?: {
    sections: HomeCustomFieldSection[]
    customFieldsbySectionId: { [key: string]: HomeCustomField[] }
  }

  currentHomeId = ''

  propertyFamilyStatus?: PropertyFamilyStatus

  currentHomeIds: string[] = []

  selectedDistributionAccount: { value: string; label: string } | undefined = undefined

  homeFieldsToVerify?: FieldVerificationProps[]

  homeDefaultBasicValues?: Partial<BasicFieldsView>

  homeDataRefreshCount = 0

  homeId = computedRouteParam('id', {
    patterns: [
      ROUTES.propertyInfo.path,
      ROUTES.propertyInHouseMonitoring.path,
      ROUTES.propertyHomeownerInfo.path,
      ROUTES.propertyCommunications.path,
      ROUTES.propertyAmenities.path,
      ROUTES.propertyValueAddedServices.path,
      ROUTES.propertyServicesAndVendors.path,
      ROUTES.propertyWorkOrderTemplates.path,
      ROUTES.propertyManual.path,
      ROUTES.propertyThingsToDo.path,
      ROUTES.propertyRooms.path,
      ROUTES.propertyLayout.path,
      ROUTES.propertyPhotoGallery.path,
      ROUTES.propertyDistributionChannels.path,
      ROUTES.propertyOnboarding.path,
      ROUTES.propertyTaxesAndFees.path,
      ROUTES.propertyEntryInformation.path,
      ROUTES.propertyStats.path,
      ROUTES.propertyManual.path,
      ROUTES.propertyInternalConfiguration.path,
      ROUTES.propertyReviews.path,
      ROUTES.propertyTaxesAndFees.path,
      ROUTES.propertyRevenueConfiguration.path,
      ROUTES.propertyFinances.path,
      ROUTES.propertyDashboard.path,
      ROUTES.propertyOwnerStatements.path,
      ROUTES.propertyPolicyAndPermits.path,
      ROUTES.propertyHistory.path,
      ...hotelTapeChartPath,
    ],
  })

  section = computedRouteParam<string | undefined>('section', {
    patterns: propertyInfoRoutes,
  })

  viewMode = computedRouteParam<IFieldsViewMode>('viewMode', {
    defaultValue: IFieldsViewMode.read,
    parse: viewMode =>
      viewMode in IFieldsViewMode ? (viewMode as IFieldsViewMode) : IFieldsViewMode.read,
    patterns: propertyInfoRoutes,
  })

  constructor() {
    makeObservable(this, {
      filterTerm: observable,
      unfilteredCustomFields: observable,
      currentHomeId: observable,
      propertyFamilyStatus: observable,
      currentHomeIds: observable,
      selectedDistributionAccount: observable,
      homeFieldsToVerify: observable,
      homeDefaultBasicValues: observable,
      homeDataRefreshCount: observable,
      homeDataFromServer: computed,
      homeHeaderDataFromServer: computed,
      isCurrentHomeAncestor: computed,
      marketId: computed,
      cancellationPolicyCategories: computed,
      selectedHomeIndicators: computed,
      selectedHomesIndicators: computed,
      selectedHomeByIds: computed,
      home: computed,
      hash: computed,
      homeInfo: computed,
      isLoadingHomeInfo: computed,
      homeHeader: computed,
      healthTags: computed,
      selectedHome: computed,
      selectedHomes: computed,
      customFields: computed,
      successors: computed,
      financialHealthDescription: computed({ keepAlive: true }),
      isSavePropertyCustomFieldsPending: computed,
      setHomeBasicValues: action.bound,
      updateHomeValuesFromServer: action.bound,
      setCurrentHomeId: action.bound,
      setCurrentHomeIds: action.bound,
    })

    this.verifyAllFields = trackedAction<any>(this.verifyAllFields.bind(this))
    this.savePropertyInfo = trackedAction<any>(this.savePropertyInfo.bind(this))
    this.searchHomesList = trackedAction<any>(this.searchHomesList.bind(this))
    this.updateHomeCancellationPolicies = trackedAction<any>(
      this.updateHomeCancellationPolicies.bind(this),
    )
    this.savePropertyCustomFields = trackedAction<any>(this.savePropertyCustomFields.bind(this))
    this.getPropertyInfoFieldsToVerify = trackedAction<any>(
      this.getPropertyInfoFieldsToVerify.bind(this),
    )
    this.getIsHomeAncestor = trackedAction<any>(this.getIsHomeAncestor.bind(this))
  }

  get homeDataFromServer() {
    void [this.homeId.get(), this.homeDataRefreshCount]
    const homeId = this.homeId.get()

    if (homeId) {
      return getPropertyById(homeId).then(
        home => {
          this.setHomeBasicValues(home)
          this.getPropertyInfoFieldsToVerify(parseHomeFieldToBasicForm(home), home?.verifyItems)

          return home
        },
        error => {
          handleStoreError({
            action: 'Failed to load the property info',
            error,
            context: { homeId },
          })
          throw error
        },
      )
    }

    return Promise.resolve(undefined)
  }

  get homeHeaderDataFromServer() {
    const homeId = this.homeId.get()

    if (homeId) {
      try {
        return getPropertyByIdHeaderInfo(homeId)
      } catch (error) {
        trackError('Property Header Info: Error getting home header info.', { error })
        Alert.Error(`Oh no! An error occurred when processing your request.`)
        return Promise.resolve(undefined)
      }
    }

    return Promise.resolve(undefined)
  }

  get isCurrentHomeAncestor() {
    const home = getValue(this.home)

    return !!home?.isAncestor
  }

  homeIsAncestor = computedFn(() => {
    return this.isCurrentHomeAncestor
  })

  get marketId() {
    return this.homeHeaderDataFromServer.then(home => home?.region?.id)
  }

  get cancellationPolicyCategories(): Promise<CancellationPolicyCategoriesResults> {
    return arriere.properties
      .getCancellationPolicyCategories()
      .then(cancellationPolicyCategories => cancellationPolicyCategories)
  }

  get selectedHomeIndicators() {
    if (this.currentHomeId) {
      const { fromDate, toDate } = getDatesFromPeriod(
        getPeriodOption(PeriodPresets.PREV_12_MONTHS).value,
      )

      return arriere.properties
        .getHomeSummaryIndicators({
          fromDate: formatDate(fromDate),
          toDate: formatDate(toDate),
          homeId: this.currentHomeId,
          granularity: Granularity.monthly,
          pagination: { page: 1, size: 1 },
        })
        .then(res => res.results[0])
    }

    return Promise.resolve(undefined)
  }

  get selectedHomesIndicators() {
    if (this.currentHomeIds.length) {
      const { fromDate, toDate } = getDatesFromPeriod(
        getPeriodOption(PeriodPresets.PREV_12_MONTHS).value,
      )

      return Promise.all(
        this.currentHomeIds.map(async homeId => {
          const res = await arriere.properties.getHomeSummaryIndicators({
            fromDate: formatDate(fromDate),
            toDate: formatDate(toDate),
            homeId,
            granularity: Granularity.monthly,
            pagination: { page: 1, size: 1 },
          })
          return res.results[0]
        }),
      )
    }

    return Promise.resolve(undefined)
  }

  get selectedHomeByIds(): Promise<HomeViewLocationSummaryProps[] | undefined> {
    if (this.currentHomeIds.length) {
      const data = this.currentHomeIds.map(async homeId => {
        return await getPropertyByIdLocationSummary(homeId)
      })

      return Promise.all(data)
    }

    return Promise.resolve(undefined)
  }

  get home() {
    return this.homeDataFromServer.then(async homeDataFromServer => {
      return homeDataFromServer
    })
  }

  get hash() {
    return getValue(this.homeHeader)?.hash
  }

  get homeInfo() {
    return getValue(this.home)
  }

  get isLoadingHomeInfo() {
    return isPending(this.homeDataFromServer)
  }

  get homeHeader() {
    void [this.homeId]

    return this.homeHeaderDataFromServer
  }

  get healthTags() {
    const homeId = this.homeId.get()

    return homeId ? getPropertyHealthTags(homeId) : Promise.resolve(undefined)
  }

  get selectedHome(): Promise<ExtendedHomeViewLocation | undefined> {
    dependsOn(this.setCurrentHomeId)

    if (this.currentHomeId) {
      return getPropertyByIdLocationSummary(this.currentHomeId).then(home => {
        return home
      })
    }

    return Promise.resolve(undefined)
  }

  get selectedHomes(): Promise<ExtendedHomeViewLocation[] | undefined> {
    return this.selectedHomeByIds
  }

  get customFields() {
    void [this.filterTerm]

    return this.home
      .then(home => {
        const customFields = home?.customFields

        return propertyInfoCustomFieldsFilter(customFields)?.map(parseSectionFields)
      })
      .then(customFields => {
        const filteredCustomFields = filterCustomFieldsByIds(customFields, [
          SQUARE_FOOTAGE_CUSTOM_FIELD_ID,
        ])

        const filteredCustomFieldSections = filterCustomFieldsBySectionIds(filteredCustomFields, [
          REVENUE_CONFIGURATION_SECTION_ID,
          ONBOARDING_SECTION_ID,
        ])

        this.unfilteredCustomFields = newNormalizeCustomFields(
          filteredCustomFieldSections,
        ) as typeof this.unfilteredCustomFields

        const filteredFieldsByTerm = filterCustomFieldsBySearchTerm(
          filteredCustomFieldSections,
          this.filterTerm,
        )

        return newNormalizeCustomFields(filteredFieldsByTerm)
      })
  }

  get successors() {
    return arriere.properties.getPropertySuccessors(this.homeId.get())
  }

  get financialHealthDescription() {
    return arriere.properties.getFinancialHealthDescription()
  }

  get isSavePropertyCustomFieldsPending() {
    return isPending(this.savePropertyCustomFields)
  }

  getHomeCustomFieldsForOTA(homeId: string, sectionId: string) {
    return arriere.properties.getHomeCustomFieldsForOTA(homeId, sectionId)
  }

  getHomeBasicValues = computedFn((home: HomeViewProps) => {
    return parseHomeFieldToBasicForm(home)
  })

  getPropertiesListForMap = computedFn(async (homes: MapHome[]) => {
    return homes.map(home => {
      const propertyTypeCustomField = getCustomFields({
        customFields: home.customFields || [],
        id: PROPERTY_TYPE_CUSTOM_FIELD_ID,
      })

      const isHotel = propertyTypeCustomField
        ? jsonParseCustomFieldValue(propertyTypeCustomField) === 'Hotel'
        : false

      const getMarkerType = () => {
        if (home.lifecycle?.guestReadinessStatus === GuestReadinessStatus.offboarded) {
          return MapMarkerType.offBoarded
        }

        if (isHotel) {
          return MapMarkerType.hotel
        }

        return MapMarkerType.vacation
      }

      return {
        ...home,
        markerType: getMarkerType(),
      }
    })
  })

  // -------- actions

  setHomeBasicValues = (home: HomeViewProps) => {
    this.homeDefaultBasicValues = parseHomeFieldToBasicForm(home)
  }

  async getIsHomeAncestor(homeId?: string) {
    if (homeId) {
      const response = await getPropertyFamilyStatus(homeId)
      this.propertyFamilyStatus = response
      return response
    }

    return Promise.resolve(undefined)
  }

  async getPropertyInfoFieldsToVerify(
    propertyBasicFields: BasicFieldsView,
    verifyItems?: HomeFieldsVerifyView[],
  ) {
    return await propertyInfoFieldsToVerify().then(response => {
      this.homeFieldsToVerify = response
        .filter(field => getValidPropertyBasicField(field.name))
        .map(field => {
          const fieldValue = Object.values(propertyBasicFields).find(
            basicField =>
              PropertyInfoFields[basicField.key as keyof typeof PropertyInfoFields] === field.name,
          )?.value

          // using toString() to include 0 as valid value
          return {
            ...field,
            value: isArray(fieldValue) ? fieldValue?.join(',') : fieldValue?.toString(),
          }
        })
        // we need to verify only fields that have defaultValue
        .filter(fieldToVerify => !isEmpty(fieldToVerify.value))
        .map(field => {
          const currentField = verifyItems?.find(homeItem => homeItem.item === field.name)

          return {
            lastVerifiedAt: currentField?.verifiedAt,
            requiresVerification: field.required,
          }
        })
    })
  }

  async savePropertyCustomFields(
    customFields: CustomFieldProjectionProps[],
    customFieldBySectionId: CustomFieldBySectionId,
    verifyCustomFields?: boolean,
    verifyAllCustomFields?: boolean,
  ) {
    const home = await this.home
    const parsedCustomFields = newParseCustomFieldsToSave(customFieldBySectionId, customFields)

    const homeId = home?.id

    if (!homeId) {
      throw new Error('Home id is not defined')
    }

    trackEvent('Property Custom Fields: Attempting to Update home info custom fields.', {
      data: {
        homeId,
      },
    })

    try {
      await arriere.properties
        .updateHomeCustomFields({
          homeId,
          customFields: parsedCustomFields,
          verifyCustomFields,
          verifyAllCustomFieldsUsed: verifyAllCustomFields,
        })
        .then(
          response => {
            if (response) {
              trackEvent(
                'Property Custom Fields: Home info custom fields was updated successfully!',
                {
                  data: {
                    homeId,
                  },
                },
              )

              Alert.Success('Home info was updated successfully')
            } else {
              trackError('Property Custom Fields: Error updating home info custom fields.', {})

              Alert.Error('Error trying to save home info')
            }
          },
          error => {
            Alert.Error(`Oops! Something went wrong: ${error}.`)
          },
        )

      await PropertyCustomFieldsStore.getHomeCustomFields(homeId)
      this.updateHomeValuesFromServer()
    } catch (error) {
      trackError('Property Custom Fields: Error updating home info custom fields.', { error })

      Alert.Error(`Oh no! An error occurred when processing your request.`)
    }
  }

  async updateHomeCancellationPolicies({ homeId, policies }: UpdateCancellationPoliciesProps) {
    trackEvent('Property Cancellation Policies: Attempting to update home cancellation policies.', {
      data: {
        homeId,
      },
    })

    try {
      await arriere.properties
        .updateHomeCancellationPolicies({
          homeId,
          policies,
        })
        .then(
          response => {
            if (response) {
              trackEvent(
                'Property Cancellation Policies: Home cancellation policies was updated successfully!',
                {
                  data: {
                    homeId,
                  },
                },
              )

              Alert.Success('Home cancellation policies was updated successfully')
            } else {
              trackError(
                'Property Cancellation Policies: Error updating home cancellation policies.',
                {},
              )

              Alert.Error('Error trying to save home cancellation policies')
            }
          },
          error => {
            Alert.Error(`Oops! Something went wrong: ${error}.`)
          },
        )

      await this.updateHomeValuesFromServer()
    } catch (error) {
      trackError('Property Cancellation Policies: Error updating home cancellation policies.', {
        error,
      })

      Alert.Error(`Oh no! An error occurred when processing your request.`)
    }
  }

  updateHomeValuesFromServer = () => {
    this.homeDataRefreshCount += 1
  }

  async searchHomesList(query: string) {
    try {
      return arriere.properties.getHomesList(query)
    } catch (error) {
      trackError('Property List: Error searching homes list.', { error })
      Alert.Error(`Oh no! An error occurred: ${error}.`)
    }
  }

  setFilterTerm = (term: string) => {
    this.filterTerm = term
  }

  async savePropertyInfo({
    name,
    title,
    shortDescription,
    description,
    address,
    checkOutTime,
    latitude,
    longitude,
    maxOccupancy,
    maxAdults,
    homeAncestors,
    bathroomsCount,
    halfBathroomsCount,
    unitNumber,
    area,
    verifications,
  }: SubmitBasicInfoProps) {
    try {
      const homeId = this.homeId.get()
      const home = await this.home
      const previousBlockedHomes = home?.blockedHomes

      const previousBuyoutIds = previousBlockedHomes?.map(blockedHome => blockedHome.id) || []
      const buyoutIds = homeAncestors || []

      trackEvent('Property Info: Attempting to update home info.', {
        data: {
          homeId,
        },
      })

      const addBuyouts = () => {
        if (isEmpty(difference(buyoutIds, previousBuyoutIds))) {
          return Promise.resolve(0)
        }

        return arriere.properties.addBuyouts({
          homeId,
          buyoutIds: difference(buyoutIds, previousBuyoutIds),
        })
      }

      const deleteBuyouts = () => {
        if (isEmpty(difference(previousBuyoutIds, buyoutIds))) {
          return Promise.resolve(0)
        }

        return arriere.properties.deleteBuyouts({
          homeId,
          buyoutIds: difference(previousBuyoutIds, buyoutIds),
        })
      }

      const updateCheckTime = () => {
        if (!checkOutTime) return Promise.resolve()

        return arriere.properties.updateCheckTime({
          homeId,
          checkOutTime,
        })
      }

      const verifyFields = () => {
        if (!verifications || isEmpty(verifications)) return Promise.resolve()

        const basicInfoVerifications = verifications.filter(
          field => field.item !== PropertyInfoFields.area,
        )

        return verifyPropertyInfo({ homeId, verifications: basicInfoVerifications })
      }

      const coordinates =
        !!latitude && !!longitude
          ? {
              coordinates: {
                latitude: Number(latitude),
                longitude: Number(longitude),
              },
            }
          : undefined

      await Promise.all([
        updateHomeData({
          homeId,
          name,
          title,
          shortDescription,
          description,
          maxOccupancy,
          maxAdults,
          bathroomsCount,
          halfBathroomsCount,
          address,
          unitNumber,
          area,
          ...coordinates,
        }),
        addBuyouts(),
        deleteBuyouts(),
        verifyFields(),
        updateCheckTime(),
      ]).then(
        response => {
          if (response) {
            trackEvent('Property Info: Home info was updated successfully!', {
              data: {
                homeId,
              },
            })

            Alert.Success('Home info was updated successfully')
          } else {
            trackError('Property Info: Error updating home info.', {})

            Alert.Error('Error trying to save home info')
          }
        },
        error => {
          trackError('Property Info: Error updating home info.', { error })

          Alert.Error(`Oops! Something went wrong: ${error}.`)
        },
      )
    } catch (error) {
      trackError('Property Info: Error updating home info.', { error })

      Alert.Error(`Oh no! An error occurred when processing your request.`)
    }

    this.updateHomeValuesFromServer()
  }

  setCurrentHomeId(homeId: string) {
    this.currentHomeId = homeId
    this.currentHomeIds = []
  }

  setCurrentHomeIds(homeIds: string[]) {
    this.currentHomeIds = homeIds
    this.currentHomeId = ''
  }

  verifyAllFields(customFields: CustomFieldProjectionProps[]) {
    // we parse fields before saving to follow parseToSave syntax
    const parseCustomFields = customFields.map(field => ({
      ...field,
      value: parseCustomFieldDefaultValue(field),
    }))

    const allRequiredFields = parseCustomFields.filter(
      field => !isFieldEmpty(field.value) && field.requiresVerification,
    )

    const customFieldBySectionId = allRequiredFields.reduce((prev, curr) => {
      if (curr.value) {
        Object.assign(prev, { [curr.id]: curr.value })
      }
      return prev
    }, {} as CustomFieldBySectionId)

    const { homeDefaultBasicValues } = this

    const verifications = homeDefaultBasicValues
      ? Object.values(homeDefaultBasicValues).map(field => ({
          item: PropertyInfoFields[field.key as keyof typeof PropertyInfoFields],
          verifiedValue: isArray(field.value) ? field.value.join(',') : field.value.toString(),
        }))
      : undefined

    Promise.all([
      this.savePropertyCustomFields(allRequiredFields, customFieldBySectionId, true, true),
      this.savePropertyInfo({ verifications }),
    ])
  }

  getHomeCustomFieldValue(fieldId: UUID) {
    const home = getValue(this.home)
    if (!home?.customFields) {
      return undefined
    }

    const fieldValue = getCustomFields({
      customFields: home.customFields || [],
      id: fieldId,
    })?.value

    return fieldValue ? JSON.parse(fieldValue) : undefined
  }
}

export const PropertyInfoStore = new _PropertyInfoStore()
